import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { getApiBaseUrl } from '../../utils/api';

type ViewMode = 'login' | 'requestReset' | 'reset';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<ViewMode>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSubmitLoading, setResetSubmitLoading] = useState(false);
  
  const { login } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('resetToken');
    if (token) {
      setMode('reset');
      setResetToken(token);
      setResetSuccessMessage('');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');
    setResetLoading(true);

    try {
      await axios.post(`${getApiBaseUrl()}/api/auth/request-password-reset`, { email: resetEmail });
      setResetMessage('Si el correo está registrado enviaremos un enlace para restablecer tu contraseña.');
    } catch (err: any) {
      if (err.response?.data?.message) {
        setResetError(err.response.data.message);
      } else {
        setResetError('No pudimos procesar tu solicitud. Intenta nuevamente.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');

    if (!resetToken) {
      setResetError('El enlace de restablecimiento no es válido. Solicita uno nuevo.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Las contraseñas no coinciden.');
      return;
    }

    setResetSubmitLoading(true);

    try {
      await axios.post(`${getApiBaseUrl()}/api/auth/reset-password`, {
        token: resetToken,
        newPassword
      });

      setResetSuccessMessage('Tu contraseña fue actualizada exitosamente. Ya puedes iniciar sesión.');
      setMode('login');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setResetError(err.response.data.message);
      } else {
        setResetError('No fue posible restablecer tu contraseña. Intenta nuevamente.');
      }
    } finally {
      setResetSubmitLoading(false);
    }
  };

  const renderFormTitle = () => {
    switch (mode) {
      case 'requestReset':
        return 'Restablecer contraseña';
      case 'reset':
        return 'Define una nueva contraseña';
      default:
        return 'Accede a tu panel de gestión';
    }
  };

  const renderFormDescription = () => {
    switch (mode) {
      case 'requestReset':
        return 'Ingresa tu correo y te enviaremos un enlace para continuar.';
      case 'reset':
        return 'Crea una contraseña segura para continuar.';
      default:
        return 'Accede a tu panel de gestión';
    }
  };

  const renderBody = () => {
    if (mode === 'requestReset') {
      return (
        <form className="mt-8 space-y-6" onSubmit={handleRequestReset}>
          <div className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                Correo electrónico
              </label>
              <input
                id="reset-email"
                name="resetEmail"
                type="email"
                autoComplete="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="input-field mt-1"
                placeholder="tu@email.com"
              />
            </div>
          </div>

          {resetError && (
            <div className="bg-red-50 border border-red-500 text-red-700 px-4 py-3 rounded-lg">
              {resetError}
            </div>
          )}

          {resetMessage && (
            <div className="bg-green-50 border border-green-500 text-green-700 px-4 py-3 rounded-lg">
              {resetMessage}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={resetLoading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {resetLoading ? 'Enviando...' : 'Enviar instrucciones'}
            </button>
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setMode('login');
                setResetError('');
                setResetMessage('');
              }}
            >
              Volver a iniciar sesión
            </button>
          </div>
        </form>
      );
    }

    if (mode === 'reset') {
      return (
        <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
          <div className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                Nueva contraseña
              </label>
              <input
                id="new-password"
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-field mt-1"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirmar contraseña
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={6}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field mt-1"
                placeholder="••••••••"
              />
            </div>
          </div>

          {resetError && (
            <div className="bg-red-50 border border-red-500 text-red-700 px-4 py-3 rounded-lg">
              {resetError}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={resetSubmitLoading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {resetSubmitLoading ? 'Actualizando...' : 'Actualizar contraseña'}
            </button>
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={() => {
                setMode('login');
                setResetError('');
                window.history.replaceState({}, document.title, window.location.pathname);
              }}
            >
              Volver a iniciar sesión
            </button>
          </div>
        </form>
      );
    }

    return (
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Correo electrónico
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field mt-1"
              placeholder="tu@email.com"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-500 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {resetSuccessMessage && (
          <div className="bg-green-50 border border-green-500 text-green-700 px-4 py-3 rounded-lg">
            {resetSuccessMessage}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex justify-center items-center"
          >
            {loading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
        </div>

        <div className="text-center space-y-2">
          <button
            type="button"
            className="text-sm text-hotel-600 hover:text-hotel-800 font-medium"
            onClick={() => {
              setMode('requestReset');
              setResetSuccessMessage('');
              setError('');
            }}
          >
            ¿Olvidaste tu contraseña?
          </button>
          <p className="text-sm text-gray-600">
            ¿Necesitas ayuda? Contacta al administrador
          </p>
        </div>
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center mb-6">
            <img 
              src="/images/logo_esstudia_inversionistas.png" 
              alt="ESSTUDIA Logo" 
              style={{ width: '28rem', height: 'auto' }}
              className="object-contain"
            />
          </div>
          <h2 className="text-center text-2xl font-semibold text-gray-900">
            {renderFormTitle()}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {renderFormDescription()}
          </p>
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default Login;
